import { useEffect, useState } from 'react';
import {
  ca, CaAction, CaAnonymizerKind, CaAnonymizerListStatus, CaAnonymizerState, CaConditions, CaDecisionRow, CaDecisionValue, CaDeviceSource,
  CaDeviceState, CaLoginDeviceState, CaMode, CaPolicy, CaPolicyInput, CaSessionLevel, CaWhatIfResult,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { errMsg } from '../utils';

const decisionColor: Record<CaDecisionValue, string> = {
  allow: 'bg-green-500/20 text-green-300',
  mfa: 'bg-yellow-500/20 text-yellow-300',
  deny: 'bg-coral/20 text-coral',
};
const decisionLabel: Record<CaDecisionValue, string> = { allow: 'erlaubt', mfa: 'MFA verlangt', deny: 'gesperrt' };
const actionLabel: Record<CaAction, string> = { deny: 'Sperren', mfa: 'MFA verlangen' };
const modeLabel: Record<CaMode, string> = { enforce: 'erzwungen', report: 'nur Bericht' };
const deviceLabel: Record<CaDeviceState, string> = { compliant: 'konform', noncompliant: 'nicht konform', unknown: 'unbekannt' };
const loginDeviceLabel: Record<CaLoginDeviceState, string> = {
  compliant: 'konform', noncompliant: 'nicht konform', none: 'ohne Zertifikat', unknown: 'unbekannt',
};
const anonymizerLabel: Record<CaAnonymizerState, string> = {
  tor: 'Tor', proxy: 'offener Proxy', vpn: 'VPN-Anbieter', none: 'kein Anonymisierungsdienst', unknown: 'unbekannt',
};
export const sessionLabel: Record<CaSessionLevel, string> = {
  off: 'keine Gerätebindung',
  report: 'Gerätebindung nur zählen',
  enforce: 'Gerätebindung durchsetzen',
  require: 'Gerätebindung verlangen',
};

/** Aktion und Sitzungssteuerung einer Richtlinie fuer die Tabelle. */
export function describeActions(p: Pick<CaPolicy, 'action' | 'sessionControls'>): string {
  const parts: string[] = [];
  if (p.action) parts.push(actionLabel[p.action]);
  if (p.sessionControls?.dbsc) parts.push(sessionLabel[p.sessionControls.dbsc]);
  return parts.join(' · ') || '—';
}

/** Komma- oder zeilengetrennte Liste; leere Eintraege fallen weg. */
export function splitList(v: string, upper = false): string[] {
  return v
    .split(/[,\n]/)
    .map((x) => x.trim())
    .filter(Boolean)
    .map((x) => (upper ? x.toUpperCase() : x));
}

/** Kurzbeschreibung der Bedingungen fuer die Tabelle. */
export function describeConditions(c: CaConditions): string {
  const parts: string[] = [];
  const u = c.users;
  if (u?.groups?.length || u?.roles?.length) parts.push(`fuer ${[...(u.groups ?? []), ...(u.roles ?? [])].join(', ')}`);
  const ex = [...(u?.excludeGroups ?? []), ...(u?.excludeRoles ?? []), ...(u?.excludeUsers ?? [])];
  if (ex.length) parts.push(`ausser ${ex.join(', ')}`);
  if (c.clients?.length) parts.push(`Anwendung ${c.clients.join(', ')}`);
  if (c.locations?.countries?.length) parts.push(`aus ${c.locations.countries.join(', ')}`);
  if (c.locations?.notCountries?.length) parts.push(`nicht aus ${c.locations.notCountries.join(', ')}`);
  if (c.locations?.untrustedOnly) parts.push('ausserhalb vertrauenswuerdiger Netze');
  if (c.risk) parts.push(`offenes Anmelderisiko ab ${c.risk.minLevel === 'high' ? 'hoch' : 'mittel'}`);
  if (c.device) {
    const base = c.device.source === 'login' ? 'Anmeldegeraet ohne Zertifikat oder nicht konform' : 'ohne konformes Geraet';
    parts.push(c.device.includeUnknown ? `${base} (auch unbekannt)` : base);
  }
  if (c.anonymizer) {
    const base = `ueber ${c.anonymizer.kinds.map((k) => anonymizerLabel[k]).join(' oder ')}`;
    parts.push(c.anonymizer.includeUnknown ? `${base} (auch ohne geladene Liste)` : base);
  }
  return parts.length ? parts.join(' · ') : 'alle Anmeldungen';
}

interface FormState {
  name: string;
  description: string;
  /** '' = keine Anmeldeaktion (reine Sitzungsrichtlinie). */
  action: CaAction | '';
  /** Geraetebindung der Sitzung; 'off' wird nicht gespeichert. */
  dbsc: CaSessionLevel;
  mode: CaMode;
  priority: string;
  groups: string;
  roles: string;
  excludeGroups: string;
  excludeUsers: string;
  clients: string;
  countries: string;
  notCountries: string;
  untrustedOnly: boolean;
  risk: '' | 'medium' | 'high';
  /** '' = egal, 'noncompliant' = ohne konformes Geraet, 'noncompliant_or_unknown' = auch wenn MDM keine Auskunft gibt. */
  device: '' | 'noncompliant' | 'noncompliant_or_unknown';
  /** 'user' = Person hat ein konformes Geraet, 'login' = Geraetezertifikat dieser Anmeldung. */
  deviceSource: CaDeviceSource;
  anonymizerTor: boolean;
  anonymizerProxy: boolean;
  anonymizerVpn: boolean;
  /** auch greifen, wenn eine gewaehlte Liste nicht geladen ist */
  anonymizerIncludeUnknown: boolean;
}

const emptyForm: FormState = {
  name: '', description: '', action: 'mfa', dbsc: 'off', mode: 'report', priority: '100', groups: '', roles: '',
  excludeGroups: '', excludeUsers: '', clients: '', countries: '', notCountries: '', untrustedOnly: false, risk: '',
  device: '', deviceSource: 'user', anonymizerTor: false, anonymizerProxy: false, anonymizerVpn: false,
  anonymizerIncludeUnknown: false,
};

export function toForm(p: CaPolicy): FormState {
  const c = p.conditions;
  return {
    name: p.name, description: p.description ?? '', action: p.action ?? '', dbsc: p.sessionControls?.dbsc ?? 'off',
    mode: p.mode, priority: String(p.priority),
    groups: (c.users?.groups ?? []).join(', '), roles: (c.users?.roles ?? []).join(', '),
    excludeGroups: (c.users?.excludeGroups ?? []).join(', '), excludeUsers: (c.users?.excludeUsers ?? []).join(', '),
    clients: (c.clients ?? []).join(', '), countries: (c.locations?.countries ?? []).join(', '),
    notCountries: (c.locations?.notCountries ?? []).join(', '), untrustedOnly: !!c.locations?.untrustedOnly,
    risk: c.risk?.minLevel ?? '',
    device: c.device ? (c.device.includeUnknown ? 'noncompliant_or_unknown' : 'noncompliant') : '',
    deviceSource: c.device?.source === 'login' ? 'login' : 'user',
    anonymizerTor: !!c.anonymizer?.kinds.includes('tor'),
    anonymizerProxy: !!c.anonymizer?.kinds.includes('proxy'),
    anonymizerVpn: !!c.anonymizer?.kinds.includes('vpn'),
    anonymizerIncludeUnknown: !!c.anonymizer?.includeUnknown,
  };
}

/** Formular in den API-Koerper; leere Gruppen fallen weg. */
export function formToInput(f: FormState, enabled = true): CaPolicyInput {
  const users: NonNullable<CaConditions['users']> = {};
  const groups = splitList(f.groups);
  const roles = splitList(f.roles);
  const excludeGroups = splitList(f.excludeGroups);
  const excludeUsers = splitList(f.excludeUsers);
  if (groups.length) users.groups = groups;
  if (roles.length) users.roles = roles;
  if (excludeGroups.length) users.excludeGroups = excludeGroups;
  if (excludeUsers.length) users.excludeUsers = excludeUsers;
  const conditions: CaConditions = {};
  if (Object.keys(users).length) conditions.users = users;
  const clients = splitList(f.clients);
  if (clients.length) conditions.clients = clients;
  const locations: NonNullable<CaConditions['locations']> = {};
  const countries = splitList(f.countries, true);
  const notCountries = splitList(f.notCountries, true);
  if (countries.length) locations.countries = countries;
  if (notCountries.length) locations.notCountries = notCountries;
  if (f.untrustedOnly) locations.untrustedOnly = true;
  if (Object.keys(locations).length) conditions.locations = locations;
  if (f.risk) conditions.risk = { minLevel: f.risk };
  if (f.device) {
    const device: NonNullable<CaConditions['device']> = { notCompliant: true };
    if (f.device === 'noncompliant_or_unknown') device.includeUnknown = true;
    // 'user' ist der Default des Backends und wird nicht mitgeschickt.
    if (f.deviceSource === 'login') device.source = 'login';
    conditions.device = device;
  }
  const kinds: CaAnonymizerKind[] = [];
  if (f.anonymizerTor) kinds.push('tor');
  if (f.anonymizerProxy) kinds.push('proxy');
  if (f.anonymizerVpn) kinds.push('vpn');
  if (kinds.length) {
    conditions.anonymizer = { kinds };
    if (f.anonymizerIncludeUnknown) conditions.anonymizer.includeUnknown = true;
  }
  return {
    name: f.name.trim(), description: f.description.trim() || null, action: f.action || null, mode: f.mode,
    priority: parseInt(f.priority, 10) || 100, enabled, conditions,
    sessionControls: f.dbsc === 'off' ? {} : { dbsc: f.dbsc },
  };
}

function fmt(ts: string): string {
  return new Date(ts).toLocaleString();
}

/** Stand der Tor-/Proxy-Listen als Hinweis unter der Bedingung. */
export function describeAnonymizerLists(s: CaAnonymizerListStatus): string {
  if (!s.configured) return 'Keine Listen konfiguriert (IGA_ANONYMIZER_SOURCES leer).';
  return s.lists
    .map((l) => `${anonymizerLabel[l.kind]}: ${l.loadedAt
      ? `${l.ranges} Bereiche, Stand ${fmt(l.loadedAt)}`
      : 'noch nicht geladen (wird bei der ersten passenden Anmeldung geholt)'}`)
    .join(' · ');
}

export default function ConditionalAccessPage() {
  const { me, hasRole } = useAuth();
  const canManage = me?.capability === 'tenant-admin' || hasRole('iga-admin');

  const [policies, setPolicies] = useState<CaPolicy[]>([]);
  const [decisions, setDecisions] = useState<CaDecisionRow[]>([]);
  const [filter, setFilter] = useState<'' | CaDecisionValue>('');
  const [form, setForm] = useState<FormState>(emptyForm);
  const [editing, setEditing] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [whatIfUser, setWhatIfUser] = useState('');
  const [whatIfGroups, setWhatIfGroups] = useState('');
  const [whatIfCountry, setWhatIfCountry] = useState('');
  const [whatIfTrusted, setWhatIfTrusted] = useState(false);
  const [whatIfRisk, setWhatIfRisk] = useState<'none' | 'medium' | 'high'>('none');
  const [whatIfDevice, setWhatIfDevice] = useState<'' | CaDeviceState>('');
  const [whatIfLoginDevice, setWhatIfLoginDevice] = useState<'' | CaLoginDeviceState>('');
  const [whatIfAnonymizer, setWhatIfAnonymizer] = useState<'' | CaAnonymizerState>('');
  const [anonymizerLists, setAnonymizerLists] = useState<CaAnonymizerListStatus | null>(null);
  const [whatIfResult, setWhatIfResult] = useState<CaWhatIfResult | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = async () => {
    setError('');
    try {
      const [p, d] = await Promise.all([ca.policies(), ca.decisions(filter ? { decision: filter } : {})]);
      setPolicies(p.data.items);
      setDecisions(d.data.items);
    } catch (err) {
      setError(errMsg(err));
    }
    // Listenstand ist nur ein Hinweis im Formular; ein Fehler blockiert die Seite nicht.
    ca.anonymizerLists().then((r) => setAnonymizerLists(r.data)).catch(() => setAnonymizerLists(null));
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setError('');
    setNotice('');
    try {
      await fn();
      if (ok) setNotice(ok);
      await load();
    } catch (err) {
      setError(errMsg(err));
    }
  };

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));

  const submit = () =>
    run(async () => {
      const body = formToInput(form, editing ? policies.find((p) => p.id === editing)?.enabled ?? true : true);
      if (editing) await ca.update(editing, body);
      else await ca.create(body);
      setShowForm(false);
      setEditing(null);
      setForm(emptyForm);
    }, editing ? 'Richtlinie gespeichert.' : 'Richtlinie angelegt.');

  const edit = (p: CaPolicy) => {
    setForm(toForm(p));
    setEditing(p.id);
    setShowForm(true);
  };

  const toggle = (p: CaPolicy, patch: Partial<CaPolicyInput>, msg: string) => run(() => ca.update(p.id, patch), msg);

  const remove = (p: CaPolicy) =>
    run(async () => {
      if (!window.confirm(`Richtlinie "${p.name}" loeschen?`)) return;
      await ca.remove(p.id);
    }, 'Richtlinie geloescht.');

  const whatIf = () =>
    run(async () => {
      const res = await ca.whatIf({
        username: whatIfUser || undefined,
        groups: splitList(whatIfGroups),
        country: whatIfCountry || undefined,
        trusted: whatIfTrusted,
        riskLevel: whatIfRisk,
        deviceState: whatIfDevice || undefined,
        loginDeviceState: whatIfLoginDevice || undefined,
        anonymizer: whatIfAnonymizer || undefined,
      });
      setWhatIfResult(res.data);
    });

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-white">Bedingter Zugriff</h2>
      <p className="text-sm text-gray-400">
        Nach dem Passwort prüft Keycloak jede Browser-Anmeldung gegen diese Richtlinien: Land, vertrauenswürdige
        Netze, offene Anmelderisiken, Gerätestatus aus dem MDM, Gruppen, Rollen und Anwendung. Beim Gerät prüft die
        Quelle „Person“, ob sie irgendein konformes Gerät hat; die Quelle „Anmeldegerät“ prüft das Gerät dieser Anmeldung
        anhand des Gerätezertifikats, das der Browser per mTLS vorlegt. „Anonymisierungsdienst“ greift bei Anmeldungen
        über einen Tor-Ausgangsknoten (Liste des Tor Project), einen bekannten offenen Proxy (FireHOL) oder das Netz eines
        VPN-Anbieters (X4BNet). Ergebnis ist{' '}
        <em>erlaubt</em>,{' '}
        <em>MFA verlangt</em> (wer keinen zweiten Faktor hat, richtet TOTP sofort ein) oder <em>gesperrt</em>.
        Neue Richtlinien starten im Modus „nur Bericht“: sie werden protokolliert, wirken aber noch nicht.
        Zusätzlich kann eine Richtlinie die Sitzung an das Gerät binden (Sitzungssteuerung, siehe unten im Formular).
      </p>
      {error && <p className="text-coral">{error}</p>}
      {notice && <p className="text-green-300">{notice}</p>}

      <section className="card overflow-x-auto">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-medium text-coral">Richtlinien</h3>
          {canManage && !showForm && (
            <button className="btn-primary" onClick={() => { setForm(emptyForm); setEditing(null); setShowForm(true); }}>
              Neue Richtlinie
            </button>
          )}
        </div>

        {showForm && canManage && (
          <div className="mb-4 grid grid-cols-1 gap-3 rounded border border-edge p-3 md:grid-cols-2">
            <label className="text-sm text-gray-400">Name
              <input className="input mt-1 w-full" value={form.name} onChange={(e) => set('name', e.target.value)} />
            </label>
            <label className="text-sm text-gray-400">Beschreibung
              <input className="input mt-1 w-full" value={form.description} onChange={(e) => set('description', e.target.value)} />
            </label>
            <label className="text-sm text-gray-400">Aktion bei der Anmeldung
              <select className="input mt-1 w-full" value={form.action} onChange={(e) => set('action', e.target.value as FormState['action'])}>
                <option value="mfa">MFA verlangen</option>
                <option value="deny">Sperren</option>
                <option value="">keine (nur Sitzungssteuerung)</option>
              </select>
            </label>
            <label className="text-sm text-gray-400">Modus
              <select className="input mt-1 w-full" value={form.mode} onChange={(e) => set('mode', e.target.value as CaMode)}>
                <option value="report">nur Bericht</option>
                <option value="enforce">erzwingen</option>
              </select>
            </label>
            <label className="text-sm text-gray-400">Gruppen (Pfade, kommagetrennt; leer = alle)
              <input className="input mt-1 w-full" value={form.groups} onChange={(e) => set('groups', e.target.value)} placeholder="/tenant-admin, /teams/FIN" />
            </label>
            <label className="text-sm text-gray-400">Realm-Rollen (leer = alle)
              <input className="input mt-1 w-full" value={form.roles} onChange={(e) => set('roles', e.target.value)} />
            </label>
            <label className="text-sm text-gray-400">Ausgenommene Gruppen
              <input className="input mt-1 w-full" value={form.excludeGroups} onChange={(e) => set('excludeGroups', e.target.value)} />
            </label>
            <label className="text-sm text-gray-400">Ausgenommene Konten (Notfallzugang!)
              <input className="input mt-1 w-full" value={form.excludeUsers} onChange={(e) => set('excludeUsers', e.target.value)} placeholder="breakglass" />
            </label>
            <label className="text-sm text-gray-400">Anwendungen (Client-IDs; leer = alle)
              <input className="input mt-1 w-full" value={form.clients} onChange={(e) => set('clients', e.target.value)} />
            </label>
            <label className="text-sm text-gray-400">Priorität (kleiner = früher)
              <input className="input mt-1 w-full" value={form.priority} onChange={(e) => set('priority', e.target.value)} />
            </label>
            <label className="text-sm text-gray-400">Nur aus Ländern (ISO, z. B. DE, AT; XX = unbekannt)
              <input className="input mt-1 w-full" value={form.countries} onChange={(e) => set('countries', e.target.value)} />
            </label>
            <label className="text-sm text-gray-400">Nicht aus Ländern (greift ausserhalb der Liste)
              <input className="input mt-1 w-full" value={form.notCountries} onChange={(e) => set('notCountries', e.target.value)} placeholder="DE, AT, CH" />
            </label>
            <label className="text-sm text-gray-400">Anmelderisiko
              <select className="input mt-1 w-full" value={form.risk} onChange={(e) => set('risk', e.target.value as FormState['risk'])}>
                <option value="">egal</option>
                <option value="medium">offener Fund ab mittel</option>
                <option value="high">offener Fund hoch</option>
              </select>
            </label>
            <label className="text-sm text-gray-400">Gerät (MDM)
              <select className="input mt-1 w-full" value={form.device} onChange={(e) => set('device', e.target.value as FormState['device'])}>
                <option value="">egal</option>
                {form.deviceSource === 'login' ? (
                  <>
                    <option value="noncompliant">ohne Gerätezertifikat oder nicht konform</option>
                    <option value="noncompliant_or_unknown">ohne Gerätezertifikat, nicht konform oder MDM ohne Auskunft</option>
                  </>
                ) : (
                  <>
                    <option value="noncompliant">ohne konformes Gerät</option>
                    <option value="noncompliant_or_unknown">ohne konformes Gerät oder MDM ohne Auskunft</option>
                  </>
                )}
              </select>
            </label>
            <label className="text-sm text-gray-400">Quelle
              <select
                className="input mt-1 w-full"
                value={form.deviceSource}
                disabled={!form.device}
                onChange={(e) => set('deviceSource', e.target.value as CaDeviceSource)}
              >
                <option value="user">Person (hat ein konformes Gerät)</option>
                <option value="login">Anmeldegerät (Gerätezertifikat)</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-400">
              <input type="checkbox" checked={form.untrustedOnly} onChange={(e) => set('untrustedOnly', e.target.checked)} />
              nur ausserhalb vertrauenswürdiger Netze
            </label>
            <fieldset className="text-sm text-gray-400 md:col-span-2">
              <legend>Anonymisierungsdienst (Anmelde-IP)</legend>
              <div className="mt-1 flex flex-wrap gap-4">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={form.anonymizerTor} onChange={(e) => set('anonymizerTor', e.target.checked)} />
                  Tor-Ausgangsknoten
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={form.anonymizerProxy} onChange={(e) => set('anonymizerProxy', e.target.checked)} />
                  offener Proxy
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={form.anonymizerVpn} onChange={(e) => set('anonymizerVpn', e.target.checked)} />
                  VPN-Anbieter
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.anonymizerIncludeUnknown}
                    disabled={!form.anonymizerTor && !form.anonymizerProxy && !form.anonymizerVpn}
                    onChange={(e) => set('anonymizerIncludeUnknown', e.target.checked)}
                  />
                  auch greifen, wenn die Liste nicht geladen ist
                </label>
              </div>
              {anonymizerLists && (
                <p className="mt-1 text-xs text-gray-500">{describeAnonymizerLists(anonymizerLists)}</p>
              )}
            </fieldset>
            <fieldset className="text-sm text-gray-400 md:col-span-2">
              <legend>Sitzungssteuerung: Sitzung an das Gerät binden</legend>
              <select
                className="input mt-1 w-full md:w-1/2"
                aria-label="Sitzung an das Gerät binden"
                value={form.dbsc}
                onChange={(e) => set('dbsc', e.target.value as CaSessionLevel)}
              >
                <option value="off">aus</option>
                <option value="report">nur zählen (Bericht, sperrt nie)</option>
                <option value="enforce">durchsetzen: kopierte Sitzungen sperren</option>
                <option value="require">verlangen: nur Browser mit Gerätebindung zulassen</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Chrome und Edge unter Windows binden die Anmeldung an einen Schlüssel im Gerät. Wird das Sitzungs-Cookie
                kopiert, etwa durch Schadsoftware, ist es auf einem anderen Gerät nach spätestens fünf Minuten wertlos.
                „Durchsetzen“ sperrt solche kopierten Sitzungen; Browser ohne Gerätebindung arbeiten weiter wie bisher.
                „Verlangen“ lässt zusätzlich nur noch Browser mit Gerätebindung zu: Firefox, Safari sowie Geräte mit
                macOS oder Linux sind dann ausgesperrt.
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Geschützt sind die Anwendungen, die über die gemeinsame Anmeldeseite (Single Sign-on) laufen. Anwendungen
                mit eigener Anmeldung werden schrittweise angeschlossen und sind bis dahin nicht gebunden. Chat und
                Passwort-Tresor speichern ihre Anmeldung im Browser selbst; sie lassen sich auf diesem Weg nicht binden.
              </p>
              {form.dbsc === 'require' && form.mode === 'enforce' && (
                <p className="mt-1 text-xs text-coral">
                  Bitte zuerst im Modus „nur Bericht“ prüfen, wen die Richtlinie träfe, und mindestens eine Bedingung
                  (z. B. eine Gruppe) setzen.
                </p>
              )}
            </fieldset>
            <div className="flex gap-2 md:col-span-2">
              <button className="btn-primary" onClick={submit}>{editing ? 'Speichern' : 'Anlegen'}</button>
              <button className="btn-ghost" onClick={() => { setShowForm(false); setEditing(null); }}>Abbrechen</button>
            </div>
          </div>
        )}

        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-400">
              <th className="p-2">Prio</th>
              <th className="p-2">Name</th>
              <th className="p-2">Bedingungen</th>
              <th className="p-2">Aktion</th>
              <th className="p-2">Modus</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {policies.length === 0 && (
              <tr><td className="p-2 text-gray-500" colSpan={6}>Keine Richtlinien, jede Anmeldung ist erlaubt.</td></tr>
            )}
            {policies.map((p) => (
              <tr key={p.id} className={`border-t border-edge align-top ${p.enabled ? '' : 'opacity-50'}`}>
                <td className="p-2">{p.priority}</td>
                <td className="p-2">
                  <div className="text-white">{p.name}</div>
                  {p.description && <div className="text-xs text-gray-500">{p.description}</div>}
                </td>
                <td className="p-2">{describeConditions(p.conditions)}</td>
                <td className="p-2">{describeActions(p)}</td>
                <td className="p-2">{p.enabled ? modeLabel[p.mode] : 'aus'}</td>
                <td className="p-2">
                  {canManage && (
                    <div className="flex flex-col gap-1">
                      <button className="btn-ghost" onClick={() => edit(p)}>Bearbeiten</button>
                      <button className="btn-ghost" onClick={() => toggle(p, { mode: p.mode === 'enforce' ? 'report' : 'enforce' }, 'Modus geändert.')}>
                        {p.mode === 'enforce' ? 'Nur Bericht' : 'Erzwingen'}
                      </button>
                      <button className="btn-ghost" onClick={() => toggle(p, { enabled: !p.enabled }, p.enabled ? 'Richtlinie ausgeschaltet.' : 'Richtlinie eingeschaltet.')}>
                        {p.enabled ? 'Ausschalten' : 'Einschalten'}
                      </button>
                      <button className="btn-ghost" onClick={() => remove(p)}>Löschen</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="card space-y-3">
        <h3 className="font-medium text-coral">Was wäre wenn?</h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <input className="input" placeholder="Benutzername" value={whatIfUser} onChange={(e) => setWhatIfUser(e.target.value)} />
          <input className="input" placeholder="Gruppen, z. B. /tenant-admin" value={whatIfGroups} onChange={(e) => setWhatIfGroups(e.target.value)} />
          <input className="input" placeholder="Land (ISO)" value={whatIfCountry} onChange={(e) => setWhatIfCountry(e.target.value)} />
          <select className="input" value={whatIfRisk} onChange={(e) => setWhatIfRisk(e.target.value as 'none' | 'medium' | 'high')}>
            <option value="none">kein offenes Risiko</option>
            <option value="medium">Risiko mittel</option>
            <option value="high">Risiko hoch</option>
          </select>
          <select className="input" value={whatIfDevice} onChange={(e) => setWhatIfDevice(e.target.value as '' | CaDeviceState)}>
            <option value="">Gerät laut MDM</option>
            <option value="compliant">Gerät konform</option>
            <option value="noncompliant">kein konformes Gerät</option>
            <option value="unknown">Gerät unbekannt</option>
          </select>
          <select className="input" value={whatIfLoginDevice} onChange={(e) => setWhatIfLoginDevice(e.target.value as '' | CaLoginDeviceState)}>
            <option value="">Anmeldegerät ohne Vorgabe</option>
            <option value="compliant">Anmeldegerät konform</option>
            <option value="noncompliant">Anmeldegerät nicht konform</option>
            <option value="none">ohne Gerätezertifikat</option>
            <option value="unknown">Anmeldegerät unbekannt</option>
          </select>
          <select className="input" value={whatIfAnonymizer} onChange={(e) => setWhatIfAnonymizer(e.target.value as '' | CaAnonymizerState)}>
            <option value="">Anonymisierungsdienst laut Liste</option>
            <option value="tor">über Tor</option>
            <option value="proxy">über offenen Proxy</option>
            <option value="vpn">über VPN-Anbieter</option>
            <option value="none">kein Anonymisierungsdienst</option>
            <option value="unknown">Liste nicht geladen</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-400">
            <input type="checkbox" checked={whatIfTrusted} onChange={(e) => setWhatIfTrusted(e.target.checked)} />
            vertrauenswürdiges Netz
          </label>
        </div>
        <button className="btn-ghost" onClick={whatIf}>Prüfen</button>
        {whatIfResult && (
          <div className="text-sm">
            <span className={`rounded px-2 py-0.5 text-xs ${decisionColor[whatIfResult.decision]}`}>{decisionLabel[whatIfResult.decision]}</span>
            {whatIfResult.reportDecision !== whatIfResult.decision && (
              <span className="ml-2 text-gray-400">mit allen Nur-Bericht-Richtlinien: {decisionLabel[whatIfResult.reportDecision]}</span>
            )}
            {whatIfResult.deviceState && (
              <span className="ml-2 text-gray-400">Gerät: {deviceLabel[whatIfResult.deviceState]}</span>
            )}
            {whatIfResult.loginDeviceState && whatIfResult.loginDeviceState !== 'unknown' && (
              <span className="ml-2 text-gray-400">Anmeldegerät: {loginDeviceLabel[whatIfResult.loginDeviceState]}</span>
            )}
            {whatIfResult.anonymizer && whatIfResult.anonymizer !== 'unknown' && (
              <span className="ml-2 text-gray-400">IP: {anonymizerLabel[whatIfResult.anonymizer]}</span>
            )}
            {whatIfResult.session && (
              <div className="mt-1 text-gray-400">
                Sitzung: {sessionLabel[whatIfResult.session.dbsc]}
                {whatIfResult.session.reportDbsc !== whatIfResult.session.dbsc && (
                  <span> (mit allen Nur-Bericht-Richtlinien: {sessionLabel[whatIfResult.session.reportDbsc]})</span>
                )}
              </div>
            )}
            <ul className="mt-2 space-y-0.5 text-gray-400">
              {whatIfResult.matched.map((m) => (
                <li key={m.id}>{m.name} ({modeLabel[m.mode]}, {actionLabel[m.action]}): {m.reasons.join(', ')}</li>
              ))}
              {(whatIfResult.session?.matched ?? []).map((m) => (
                <li key={`s-${m.id}`}>{m.name} ({modeLabel[m.mode]}, {sessionLabel[m.dbsc]}): {m.reasons.join(', ')}</li>
              ))}
              {whatIfResult.matched.length === 0 && !whatIfResult.session?.matched.length && <li>Keine Richtlinie greift.</li>}
            </ul>
          </div>
        )}
      </section>

      <section className="card overflow-x-auto">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-medium text-coral">Anmeldeprotokoll</h3>
          <select className="input" value={filter} onChange={(e) => setFilter(e.target.value as '' | CaDecisionValue)}>
            <option value="">alle</option>
            <option value="deny">gesperrt</option>
            <option value="mfa">MFA verlangt</option>
            <option value="allow">erlaubt</option>
          </select>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-400">
              <th className="p-2">Zeit</th>
              <th className="p-2">Person</th>
              <th className="p-2">Ort</th>
              <th className="p-2">Anwendung</th>
              <th className="p-2">Entscheidung</th>
              <th className="p-2">Richtlinien</th>
            </tr>
          </thead>
          <tbody>
            {decisions.length === 0 && (
              <tr><td className="p-2 text-gray-500" colSpan={6}>Keine Einträge.</td></tr>
            )}
            {decisions.map((d) => (
              <tr key={d.id} className="border-t border-edge align-top">
                <td className="p-2 whitespace-nowrap">{fmt(d.decided_at)}</td>
                <td className="p-2">{d.username || d.user_id}</td>
                <td className="p-2">
                  {[d.country, d.ip].filter(Boolean).join(' · ') || '—'}
                  {d.device_state && d.device_state !== 'unknown' && (
                    <div className="text-xs text-gray-500">Gerät {deviceLabel[d.device_state]}</div>
                  )}
                  {d.login_device_state && d.login_device_state !== 'unknown' && (
                    <div className="text-xs text-gray-500">Anmeldegerät {loginDeviceLabel[d.login_device_state]}</div>
                  )}
                  {d.anonymizer && d.anonymizer !== 'none' && d.anonymizer !== 'unknown' && (
                    <div className="text-xs text-coral">über {anonymizerLabel[d.anonymizer]}</div>
                  )}
                </td>
                <td className="p-2">{d.client_id || '—'}</td>
                <td className="p-2">
                  <span className={`rounded px-2 py-0.5 text-xs ${decisionColor[d.decision]}`}>{decisionLabel[d.decision]}</span>
                  {d.report_decision !== d.decision && (
                    <div className="text-xs text-gray-500">Bericht: {decisionLabel[d.report_decision]}</div>
                  )}
                </td>
                <td className="p-2 text-xs text-gray-400">
                  {d.matched.map((m) => `${m.name} (${modeLabel[m.mode]})`).join(', ') || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
